<template>
  <div
    class="flex flex-col rounded-lg overflow-hidden py-2 px-2 md:px-4 bg-white dark:bg-gray-950 dark:text-white border dark:border-gray-700 min-h-full flex-grow"
  >
    <div class="text-base md:text-lg">Total Users</div>
    <div v-if="loading" class="flex flex-1 justify-center">
      <StyledLoader />
    </div>
    <div v-else class="flex-1 grid">
      <div
        class="flex justify-center items-center flex-col lg:flex-row w-full"
        v-if="data.length"
      >
        <div class="lg:w-2/3">
          <apexchart
            type="donut"
            :options="chartOptions"
            :series="chartSeries"
          ></apexchart>
        </div>
        <div class="text-sm flex lg:flex-col flex-wrap lg:space-y-2">
          <span
            v-for="(item, index) in labelData"
            :key="index"
            :style="{ color: colors[index] }"
          >
            ● {{ item }}
          </span>
        </div>
      </div>
      <div v-else class="flex justify-center items-center text-base md:text-lg">
        No activity.
      </div>
    </div>
    <SourceBadge
      src="/images/google_analytics_4.png"
      alt="Google Analytics"
      label="Google Analytics"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import Api from '@/utils/ApiClient';
import { formatNumber } from '@/utils/helperFunctions';
import { useTeamStore } from '@/stores/teamStore';
import { useDateStore } from '@/stores/dateStore';
import StyledLoader from '@/components/StyledLoader.vue';
import SourceBadge from '@/components/SourceBadge.vue';

const data = ref();
const labelData = ref();
const chartLabels = ref([]);
const dateStore = useDateStore();
const teamStore = useTeamStore();
const currentTeam = ref(teamStore.current_team);
const loading = ref(false);
const colors = ['#fbbf24', '#f59e0b', '#34d399', '#60a5fa', '#e11d48'];
const chartOptions = computed(() => ({
  chart: {
    id: 'users-chart',
    type: 'donut',
    toolbar: {
      show: false,
    },
    zoom: {
      enabled: false,
    },
  },
  colors: colors,
  dataLabels: {
    enabled: false,
  },
  legend: {
    show: false,
  },
  tooltip: {
    theme: 'dark',
  },
  noData: {
    text: 'No Activity',
  },
  labels: chartLabels.value,
}));

const chartSeries = ref([0, 0]);

const fetchData = async () => {
  try {
    loading.value = true;
    const response = await Api.get(
      `/google-analytics/first-user-source-metrics`,
      {
        params: {
          start_date: dateStore?.dashboardDate?.currentStart,
          end_date: dateStore?.dashboardDate?.currentEnd,
        },
      },
    );
    data.value = JSON.parse(response.data?.items.data);
    if (Array.isArray(data.value) && data.value.length > 0) {
      const labels = data.value.map((item) => item[0]);
      const series = data.value.map((item) => item[1]);

      labelData.value = data.value.map(
        (item) =>
          item[0].toUpperCase().slice(0, 8) + '-' + formatNumber(item[1]),
      );

      chartLabels.value = labels;
      chartSeries.value = series;
    }
  } catch (error) {
    data.value = [];
    labelData.value = [];
    chartLabels.value = [];
    chartSeries.value = [];
  } finally {
    loading.value = false;
  }
};
watch(
  [dateStore.dashboardDate],
  async () => {
    await fetchData();
  },
  { immediate: true, deep: true },
);

watch(
  () => teamStore.current_team,
  (newTeam) => {
    if (newTeam) {
      currentTeam.value = newTeam;
      fetchData();
    }
  },
  {
    immediate: true,
    deep: true,
  },
);
</script>
